import { useState } from "react";
import { DatabaseBackup, Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { api, dt, errMsg } from "@/lib/api";
import { ErrorBox, Field, Modal, PageHeader } from "@/components/common";

export default function Backup() {
  const hist = usePoll("/backup/history", null, 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [pw, setPw] = useState("");
  const [txt, setTxt] = useState("");
  const lastOk = (hist.data || []).find((h) => h.type === "export" && h.status === "success");

  const exportNow = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/backup/export", { responseType: "blob", timeout: 120000 });
      const name = r.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "souk-el-kabch-backup.json";
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      toast.success("تم إنشاء النسخة الاحتياطية وتنزيلها – احفظ الملف في مكان آمن"); hist.reload();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const pick = async (e) => {
    setErr(""); setParsed(null);
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f);
    try { const j = JSON.parse(await f.text()); if (j.app !== "souk-el-kabch") throw new Error(); setParsed(j); } catch { setErr("الملف ليس نسخة احتياطية صالحة لسوق الكبش"); }
  };
  const restore = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/backup/restore", { password: pw, confirm_text: txt, backup: parsed }, { timeout: 180000 });
      toast.success("تمت استعادة البيانات بنجاح"); setConfirm(false); setPw(""); setTxt(""); hist.reload();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title="النسخ الاحتياطي واستعادة البيانات" subtitle="البيانات محفوظة في قاعدة البيانات المركزية؛ النسخة الاحتياطية ملف تحفظه خارج الخادم" />
      <section className="k-card p-6 space-y-3">
        <div className="flex items-center gap-3"><DatabaseBackup className="w-6 h-6 text-[#1A4D2E]" /><h2 className="font-display font-bold text-lg">إنشاء نسخة احتياطية</h2></div>
        <p className="text-sm text-slate-600" data-testid="last-backup-info">آخر نسخة ناجحة: <b>{lastOk ? `${dt(lastOk.created_at)} – ${lastOk.by}` : "لا توجد"}</b></p>
        <button className="k-btn-primary" onClick={exportNow} disabled={busy} data-testid="backup-export-button">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} إنشاء وتنزيل نسخة احتياطية</button>
      </section>
      <section className="k-card p-6 space-y-3 border-rose-200">
        <h2 className="font-display font-bold text-lg text-rose-800">استعادة البيانات</h2>
        <p className="text-sm text-slate-600">تستبدل الاستعادة جميع البيانات الحالية ببيانات الملف. يحتفظ الخادم بلقطة من البيانات الحالية قبل الاستعادة.</p>
        <label className="k-btn-outline cursor-pointer w-fit"><Upload className="w-4 h-4" /> اختيار ملف النسخة<input type="file" accept="application/json,.json" className="hidden" onChange={pick} data-testid="restore-file-input" /></label>
        {parsed && (
          <div className="rounded-xl bg-[#F6F1E7] p-3 text-sm" data-testid="restore-file-summary">
            <b>{file?.name}</b> – أنشئت في {dt(parsed.created_at)} بواسطة {parsed.by}
            <div className="text-xs mt-1">{Object.entries(parsed.counts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
            <button className="k-btn-danger k-btn-sm mt-3" onClick={() => setConfirm(true)} data-testid="restore-open-confirm">استعادة هذه النسخة</button>
          </div>
        )}
        <ErrorBox text={err} />
      </section>
      <section className="k-card overflow-x-auto">
        <div className="px-5 py-4 font-display font-bold">سجل عمليات النسخ والاستعادة</div>
        <table className="k-table" data-testid="backup-history-table"><thead><tr><th>النوع</th><th>التاريخ</th><th>بواسطة</th><th>الحالة</th><th>عدد السجلات</th></tr></thead>
          <tbody>{(hist.data || []).map((h) => <tr key={h.id}><td>{h.type === "export" ? "نسخ احتياطي" : "استعادة"}</td><td>{dt(h.created_at)}</td><td>{h.by}</td>
            <td className={h.status === "success" ? "text-emerald-700 font-bold" : h.status === "failed" ? "text-rose-700 font-bold" : ""}>{h.status === "success" ? "ناجحة" : h.status === "failed" ? "فاشلة" : "جارية"}</td>
            <td className="text-xs">{h.counts ? Object.values(h.counts).reduce((a, b) => a + b, 0) : "—"}</td></tr>)}</tbody></table>
      </section>
      <Modal open={confirm} onOpenChange={setConfirm} title="تأكيد استعادة البيانات" description="هذه عملية حساسة لا يمكن التراجع عنها من الواجهة"
        footer={<button className="k-btn-danger" disabled={busy || txt !== "استعادة" || !pw} onClick={restore} data-testid="restore-confirm-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} تأكيد الاستعادة</button>}>
        <Field label="كلمة مرور حسابك"><input type="password" className="k-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="restore-password-input" /></Field>
        <Field label="اكتب كلمة «استعادة» للتأكيد"><input className="k-input" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="restore-confirm-text" /></Field>
        <ErrorBox text={err} />
      </Modal>
    </div>
  );
}
